import React, { useCallback } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { RFeature, RLayerVectorImage } from "rlayers";
import { LineString, Point } from "ol/geom";
import { transform } from "ol/proj";
import VectorLayer from '../../map/layers/VectorLayer';
import { store } from '../../../../app/store';
import { RootState } from "../../../../app/store";
import { adsbStyleFunction } from "../styles/adsb";
import { aeroActions } from '../../../../redux/aero/aeroSlice';
import { createSelector } from "@reduxjs/toolkit";

const selectLastPositions = createSelector(
    (state: RootState) => Object.keys(state.nav.tracking).map(key => ({plid: key, source: state.nav.tracking[key].source})),
    (state: RootState) => state.telemetry.streams,
    (mobiles: {plid: string, source: string | null}[], streams: any) => {
        const lastPositions: {[key: string]: any} = {};
        mobiles.forEach(mobile => {
            lastPositions[mobile.plid] = mobile.source ? streams[mobile.plid]?.[mobile.source]?.[1]?.slice(-1)[0] : undefined;
        });
        return lastPositions;
    }
);

const Aircraft = React.memo(function Aircraft({ id, position, properties, hud, filters }: { id: string, position: number[], properties: any, hud: any, filters: any }) {
    const dispatch = useDispatch();
    const mapProj = store.getState().map.projection;
    const geometry = new Point(transform(position, "EPSG:4326", mapProj));

    const featureLifetime: number | undefined = useSelector((state: RootState) =>
        state.mission.activeConfig?.["aero"]?.["adsb"]?.[properties?.callsign ?? ""]?.["feature_lifetime"]
        ?? state.mission.activeConfig?.["aero"]?.["adsb"]?.default?.["feature_lifetime"]
    );
    const lastPositions = useSelector(selectLastPositions);

    const onSingleClick = useCallback((e: any) => {
        dispatch(aeroActions.adsb_aircraft_click({ id, properties }));
        return false; // prevent forwarding click event to other features
    }, [dispatch, id, properties]);

    const onDblClick = useCallback(() => {
        dispatch(aeroActions.adsb_aircraft_dblClick({ id, properties }));
        return false; // prevent zooming in on double click
    }, [dispatch, id, properties]);

    const styleFunc = useCallback((feature: any, resolution: any) => {
        return adsbStyleFunction(feature, resolution, filters, hud, lastPositions, featureLifetime);
    }, [hud, filters, lastPositions, featureLifetime]);

    return (
        <RFeature
            key={id}
            geometry={geometry}
            properties={properties}
            onSingleClick={onSingleClick}
            onDblClick={onDblClick}
            style={styleFunc}
        />
    );
});

const Bbox = React.memo(function Bbox({ id, bbox }: { id: string, bbox: {min_lon: number, min_lat: number, max_lon: number, max_lat: number}}) {
    const mapProj = store.getState().map.projection;
    const positions = [
        [bbox.min_lon, bbox.min_lat],
        [bbox.max_lon, bbox.min_lat],
        [bbox.max_lon, bbox.max_lat],
        [bbox.min_lon, bbox.max_lat],
        [bbox.min_lon, bbox.min_lat]
    ];
    const geometry = new LineString(positions.map(position => transform(position, "EPSG:4326", mapProj)))

    return (
        <RFeature
            key={id}
            geometry={geometry}
            // TODO: add style func ?
        />
    )
});

export default function Adsb({ zIndex }: { zIndex: number }) {
    const adsb = useSelector((state: RootState) => state.nav.aero.adsb);

    return (
        <VectorLayer
            id="adsb-layer"
            component={RLayerVectorImage}
            zIndex={zIndex}
            visible={adsb.visible}
            rulingSnap={true}
        >
            {Object.entries(adsb.bbox).map(([source, bbox], index) =>
                <Bbox key={source + "-bbox"} id={source + "-bbox"} bbox={bbox}/>
            )}
            {adsb.list.map((a, index) =>
                <Aircraft key={a.id} id={a.id} position={a.geometry.coordinates} properties={a.properties} hud={a.hud} filters={adsb.filters} />
            )}
        </VectorLayer>
    );

};